class CalderaHeroSlider extends HTMLElement {
  /** @type {HTMLElement | null} */
  #track = null;

  /** @type {HTMLElement[]} */
  #realSlides = [];

  /** @type {HTMLElement | null} */
  #leftClone = null;

  /** @type {HTMLElement | null} */
  #rightClone = null;

  #current = 0;
  #wrapping = false;

  /** @type {ReturnType<typeof setInterval> | null} */
  #autoplayId = null;

  #rafId = 0;

  #onScroll = () => {
    cancelAnimationFrame(this.#rafId);
    this.#rafId = requestAnimationFrame(() => this.#updateCenterSlide());
  };

  #onResize = () => {
    cancelAnimationFrame(this.#rafId);
    this.#rafId = requestAnimationFrame(() => {
      const realSlide = this.#realSlides[this.#current];
      if (realSlide) this.#scrollToSlide(realSlide, true);
      this.#updateCenterSlide();
    });
  };

  #onPointerEnter = () => this.#stopAutoplay();
  #onPointerLeave = () => this.#startAutoplay();

  connectedCallback() {
    this.#track = this.querySelector('[data-track]');
    if (!this.#track) return;

    requestAnimationFrame(() => {
      this.#initSlides();
      this.#wireUpControls();
      this.#updateCenterSlide();
      this.#updateDots();
      this.#startAutoplay();
    });
  }

  disconnectedCallback() {
    this.#stopAutoplay();
    cancelAnimationFrame(this.#rafId);
    this.#track?.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('resize', this.#onResize);
    this.removeEventListener('pointerenter', this.#onPointerEnter);
    this.removeEventListener('pointerleave', this.#onPointerLeave);
  }

  #initSlides() {
    const track = this.#track;
    if (!track) return;

    const slides = /** @type {HTMLElement[]} */ (
      Array.from(track.querySelectorAll('slideshow-slide:not([data-clone])'))
    );
    if (slides.length < 2) return;

    this.#realSlides = slides;

    const firstReal = slides[0];
    const lastReal = slides[slides.length - 1];
    if (!firstReal || !lastReal) return;

    // Append clone of first real → right wrap target.
    const firstClone = this.#makeClone(firstReal);
    track.appendChild(firstClone);
    this.#rightClone = firstClone;

    // Prepend clone of last real → left wrap target.
    const lastClone = this.#makeClone(lastReal);
    track.insertBefore(lastClone, firstReal);
    this.#leftClone = lastClone;

    // For 3+ slides, add an extra buffer clone on each side so the wrap-target
    // clones have a peek slide of their own and can be centered without the
    // browser clamping scrollLeft at 0 / scrollWidth (which causes a visible jerk).
    if (slides.length >= 3) {
      const second = slides[1];
      const secondLast = slides[slides.length - 2];
      if (second) track.appendChild(this.#makeClone(second));
      if (secondLast) track.insertBefore(this.#makeClone(secondLast), lastClone);
    }

    this.#scrollToSlide(firstReal, true);
  }

  /**
   * @param {HTMLElement} source
   * @returns {HTMLElement}
   */
  #makeClone(source) {
    const clone = /** @type {HTMLElement} */ (source.cloneNode(true));
    clone.setAttribute('data-clone', 'true');
    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('id');
    for (const el of clone.querySelectorAll('[id]')) el.removeAttribute('id');
    return clone;
  }

  #wireUpControls() {
    const track = this.#track;
    if (!track) return;

    const prev = this.querySelector('[data-arrow-prev]');
    const next = this.querySelector('[data-arrow-next]');

    prev?.addEventListener('click', () => this.previous());
    next?.addEventListener('click', () => this.next());

    for (const dot of this.querySelectorAll('[data-dot-index]')) {
      const idx = parseInt(dot.getAttribute('data-dot-index') || '0', 10);
      dot.addEventListener('click', () => this.select(idx));
    }

    track.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#onResize, { passive: true });
    this.addEventListener('pointerenter', this.#onPointerEnter);
    this.addEventListener('pointerleave', this.#onPointerLeave);
  }

  next() {
    this.select(this.#current + 1);
  }

  previous() {
    this.select(this.#current - 1);
  }

  /**
   * @param {number} idx — may be negative or > total to trigger wrap.
   */
  select(idx) {
    if (this.#wrapping) return;

    const total = this.#realSlides.length;
    if (!total) return;

    const lastIdx = total - 1;
    const wantsForward = idx > lastIdx;
    const wantsBackward = idx < 0;
    const realIdx = ((idx % total) + total) % total;
    const realSlide = this.#realSlides[realIdx];
    if (!realSlide) return;

    this.#current = realIdx;
    this.#updateDots();

    if (wantsForward && this.#rightClone) {
      this.#scrollToSlide(this.#rightClone, false);
      this.#scheduleCloneJump(realSlide);
    } else if (wantsBackward && this.#leftClone) {
      this.#scrollToSlide(this.#leftClone, false);
      this.#scheduleCloneJump(realSlide);
    } else {
      this.#scrollToSlide(realSlide, false);
    }
  }

  /**
   * @param {HTMLElement} slide
   * @param {boolean} instant
   */
  #scrollToSlide(slide, instant) {
    const track = this.#track;
    if (!track) return;
    const trackWidth = track.clientWidth;
    const slideWidth = slide.offsetWidth;
    const targetLeft = slide.offsetLeft - (trackWidth - slideWidth) / 2;

    track.scrollTo({
      left: targetLeft,
      behavior: instant ? 'instant' : 'smooth',
    });
  }

  /**
   * @param {HTMLElement} realSlide
   */
  #scheduleCloneJump(realSlide) {
    const track = this.#track;
    if (this.#wrapping || !track) return;
    this.#wrapping = true;

    const finish = () => {
      this.#scrollToSlide(realSlide, true);
      this.#wrapping = false;
    };

    if ('onscrollend' in window) {
      track.addEventListener('scrollend', finish, { once: true });
    } else {
      setTimeout(finish, 700);
    }
  }

  #updateCenterSlide() {
    const track = this.#track;
    if (!track) return;
    const slides = track.querySelectorAll('slideshow-slide');
    if (!slides.length) return;

    const trackRect = track.getBoundingClientRect();
    const trackCenter = trackRect.left + trackRect.width / 2;

    /** @type {Element | null} */
    let closest = null;
    let minDistance = Infinity;

    for (const slide of slides) {
      const rect = slide.getBoundingClientRect();
      const slideCenter = rect.left + rect.width / 2;
      const distance = Math.abs(slideCenter - trackCenter);
      if (distance < minDistance) {
        minDistance = distance;
        closest = slide;
      }
    }

    for (const slide of slides) {
      slide.toggleAttribute('data-center', slide === closest);
    }
  }

  #updateDots() {
    for (const dot of this.querySelectorAll('[data-dot-index]')) {
      const idx = parseInt(dot.getAttribute('data-dot-index') || '0', 10);
      if (idx === this.#current) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    }
  }

  #startAutoplay() {
    const enabled = this.dataset.autoplayEnabled === 'true';
    const delay = parseInt(this.dataset.autoplayDelay || '0', 10);
    if (!enabled || !delay || this.#realSlides.length < 2) return;

    this.#stopAutoplay();
    this.#autoplayId = setInterval(() => this.next(), delay);
  }

  #stopAutoplay() {
    if (this.#autoplayId !== null) {
      clearInterval(this.#autoplayId);
      this.#autoplayId = null;
    }
  }
}

customElements.define('caldera-hero-slider', CalderaHeroSlider);
